/**
 * TestConnectionButton Component
 *
 * Button that triggers a test connection to verify spreadsheet access.
 * Shows loading state during test and displays success/error results.
 */

import type { TestConnectionResult } from '@/types/accounting';

interface TestConnectionButtonProps {
  onTest: () => void;
  isLoading: boolean;
  result: TestConnectionResult | null;
  disabled?: boolean;
}

export function TestConnectionButton({
  onTest,
  isLoading,
  result,
  disabled = false,
}: TestConnectionButtonProps) {
  return (
    <div>
      {/* Test Button */}
      <button
        type="button"
        onClick={onTest}
        disabled={disabled || isLoading}
        className="inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {isLoading ? (
          <>
            <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
                fill="none"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
              />
            </svg>
            Testing Connection...
          </>
        ) : (
          <>
            <svg
              className="w-4 h-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            Test Connection
          </>
        )}
      </button>

      {/* Result Display */}
      {result && (
        <div className="mt-4">
          {result.success ? (
            <TestConnectionSuccess result={result} />
          ) : (
            <TestConnectionError error={result.error || 'Connection failed'} />
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Success result display
 */
function TestConnectionSuccess({ result }: { result: TestConnectionResult }) {
  return (
    <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
      {/* Success Header */}
      <div className="flex items-center gap-2 text-green-700 font-semibold mb-2">
        <svg
          className="w-5 h-5"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2}
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
        Connection Successful
      </div>

      {/* Row Count */}
      <p className="text-sm text-green-700 mb-3">
        Found {result.rowCount?.toLocaleString()} rows in spreadsheet.
      </p>

      {/* Sample Row */}
      {result.sampleRow && result.sampleRow.length > 0 && (
        <div className="mt-3 p-3 bg-white/50 rounded-md overflow-x-auto">
          <span className="text-xs font-medium text-green-600 block mb-1">Sample row:</span>
          <code className="text-xs font-mono text-green-800 whitespace-nowrap">
            {result.sampleRow.slice(0, 6).join(' | ')}
            {result.sampleRow.length > 6 && ' ...'}
          </code>
        </div>
      )}

      {/* Detected Accounts */}
      {result.detectedAccounts && result.detectedAccounts.length > 0 && (
        <div className="mt-4 p-3 bg-white border border-green-200 rounded-lg">
          <div className="flex items-center gap-2 text-green-700 font-medium text-sm mb-2">
            <svg
              className="w-4 h-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4" />
            </svg>
            Detected Bank Accounts ({result.detectedAccounts.length})
          </div>
          <div className="flex flex-wrap gap-2">
            {result.detectedAccounts.map((account) => (
              <span
                key={account}
                className="inline-block px-2.5 py-1 bg-white border border-green-200 rounded text-xs font-mono text-gray-700"
              >
                {account}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Error result display
 */
function TestConnectionError({ error }: { error: string }) {
  return (
    <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
      {/* Error Header */}
      <div className="flex items-center gap-2 text-red-700 font-semibold mb-2">
        <svg
          className="w-5 h-5"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2}
        >
          <circle cx="12" cy="12" r="10" />
          <path strokeLinecap="round" strokeLinejoin="round" d="M15 9l-6 6M9 9l6 6" />
        </svg>
        Connection Failed
      </div>

      {/* Error Message */}
      <p className="text-sm text-red-700">{error}</p>

      {/* Troubleshooting Tips */}
      <div className="mt-3 text-sm text-red-600">
        <p className="font-medium mb-1">Please check:</p>
        <ul className="list-disc list-inside space-y-0.5 ml-2">
          <li>The spreadsheet ID is correct</li>
          <li>The sheet name exists</li>
          <li>Your Google account has access to this spreadsheet</li>
        </ul>
      </div>
    </div>
  );
}
